import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Info } from 'lucide-react';

import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { BADGE_TOPICS } from '@/data/badges';

// Minimum agents who must have attempted a topic before its average is shown.
// With a team of 11, anything lower starts to identify individuals.
const MIN_AGENTS = 4;

const labelFor = (key) =>
  BADGE_TOPICS.find((t) => t.key === key)?.label ||
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Colours come from the design tokens in index.css, so this follows the
// Stellar palette and adapts to dark mode without a second set of values.
function bandFor(score) {
  if (score === null || score === undefined)
    return { bar: 'bg-muted', text: 'text-muted-foreground', note: null };
  if (score < 50)
    return { bar: 'bg-destructive', text: 'text-destructive', note: 'Needs attention' };
  if (score < 70)
    return { bar: 'bg-secondary', text: 'text-secondary', note: 'Worth revisiting' };
  if (score < 85)
    return { bar: 'bg-accent', text: 'text-accent', note: 'Solid' };
  return { bar: 'bg-primary', text: 'text-primary', note: 'Strong' };
}

function TopicRow({ row }) {
  const score = row.avg_best_score === null ? null : Number(row.avg_best_score);
  const band = bandFor(score);
  const coverage =
    row.total_agents > 0
      ? Math.round((Number(row.agents_trained) / Number(row.total_agents)) * 100)
      : 0;

  return (
    <li className="px-5 py-4 rounded-2xl border border-border bg-card">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {labelFor(row.topic)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {row.agents_trained} of {row.total_agents} agents trained
            {band.note && row.enough_data && ` · ${band.note}`}
          </p>
        </div>

        <span className={cn('text-2xl font-semibold tabular-nums', band.text)}>
          {row.enough_data ? `${Math.round(score)}%` : '—'}
        </span>
      </div>

      {row.enough_data ? (
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-700', band.bar)}
            style={{ width: `${Math.max(2, Math.min(100, score))}%` }}
          />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          Not enough data yet — averages appear once at least {MIN_AGENTS} agents
          have trained on this topic.
        </p>
      )}

      <div className="mt-2.5 flex items-center gap-1.5">
        <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-muted-foreground/40"
            style={{ width: `${coverage}%` }}
          />
        </div>
        <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
          {coverage}% covered
        </span>
      </div>
    </li>
  );
}

export default function Team() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let active = true;
    supabase
      .rpc('get_team_topic_stats', { p_min_agents: MIN_AGENTS })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          console.error('Team stats failed:', error.message);
          setRows([]);
          return;
        }
        setRows(data ?? []);
      });
    return () => {
      active = false;
    };
  }, []);

  const loading = rows === null;
  const list = rows || [];
  const weakest = list.find((r) => r.enough_data);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1.5 w-4 h-4" />
            Back to quiz
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tracking-tight text-foreground">
              Team progress
            </span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 text-accent mb-5">
            <Users className="w-8 h-8" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            Where the team stands
          </h1>
          <p className="mt-2 text-muted-foreground">
            Average best score per topic, across everyone. Weakest first.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No training sessions recorded yet.
          </div>
        ) : (
          <>
            {weakest && (
              <div className="mb-6 p-4 rounded-2xl border border-border bg-muted/40">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
                  Focus here first
                </p>
                <p className="text-sm text-foreground">
                  <span className="font-semibold">{labelFor(weakest.topic)}</span> is
                  the team's weakest topic at{' '}
                  {Math.round(Number(weakest.avg_best_score))}%.
                </p>
              </div>
            )}

            <ol className="space-y-3">
              {list.map((row) => (
                <TopicRow key={row.topic} row={row} />
              ))}
            </ol>
          </>
        )}

        <div className="mt-10 flex items-start gap-2.5 p-4 rounded-xl border border-dashed border-border">
          <Info className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            These are group averages only. Individual results are never shown
            here, and a topic stays hidden until at least {MIN_AGENTS} agents
            have attempted it. Each person's best score per topic is counted
            once, so practising a topic repeatedly does not drag the average
            down.
          </p>
        </div>
      </main>
    </div>
  );
}
